import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ErrorBox, LoadingContainer } from '../../../components';
import { useStore } from '../../../store';
import { PulseCdnConnectDto } from '../../account/store/services/storages/PulseCdnStorage.ts';
import S3File from '../../products/edit/files/store/S3File.ts';
import styles from '../styles.css?raw';
import type { PulseCdnBrowser, PulseCdnOptions } from '../types.ts';
import { useCss } from '../utils/loadAssets.ts';
import loadPulseCdn from '../utils/loadPulseCdn.ts';

export default observer(function PulseCdnFramePage() {
  const { '*': initialPath = '/' } = useParams();
  const [searchParams] = useSearchParams();
  useCss(styles);
  const { cdnLoadError, cdnLoading } = usePulseCdn({
    initialPath,
    pulseCdnOptions: parsePulseCdnOptions(searchParams),
  });

  const isCdnHidden = Boolean(cdnLoading || cdnLoadError);
  const isLoaderVisible = cdnLoading && !cdnLoadError;

  return (
    <>
      {isLoaderVisible && <LoadingContainer />}
      {cdnLoadError && <ErrorBox>{cdnLoadError}</ErrorBox>}
      <div
        id="bucketblaze-file-browser"
        style={{ display: isCdnHidden ? 'none' : 'block' }}
      />
    </>
  );
});

async function initializePulseCdn({
  initialPath = '/',
  getOptions,
  pulseCdnOptions,
  selectedFiles = [],
}: {
  getOptions(): Promise<PulseCdnConnectDto>;
  initialPath?: string;
  pulseCdnOptions?: PulseCdnOptions;
  selectedFiles?: S3File[];
}) {
  const cdnBrowser = await loadPulseCdn({
    getOptions,
    initialPath,
    pulseCdnOptions,
  });

  if (selectedFiles.length > 0) {
    cdnBrowser.setSelectedFiles(selectedFiles);
  }

  cdnBrowser.onLocationChange((value) => {
    console.log('Location changed:', value);
    window.parent.postMessage({ type: 'cdnOnLocationChange', value });
    window.parent.postMessage({ type: 'cdnOnSelectFiles', value: [] });
  });

  cdnBrowser.onSelectionChange((value) => {
    console.log('Selection changed:', value);
    window.parent.postMessage({ type: 'cdnOnSelectFiles', value });
  });

  cdnBrowser.onDialog((shown) => {
    // In content mode, let dialogs (e.g. upload) fill the iframe
    if (pulseCdnOptions?.heightMode === 'content') {
      document
        .getElementById('bucketblaze-file-browser')
        ?.classList.toggle('bb-fill', shown);
    }
    window.parent.postMessage({ type: 'cdnOnDialog', value: shown });
  });

  cdnBrowser.onUpload((inProgress) => {
    window.parent.postMessage({ type: 'cdnOnUpload', value: inProgress });
  });

  window.addEventListener('message', (e: MessageEvent) => {
    if (e.data.type === 'cdnSetLocation') {
      cdnBrowser.navigate(e.data.value);
    }
  });

  return cdnBrowser;
}

function usePulseCdn({
  initialPath = '/',
  pulseCdnOptions,
}: {
  initialPath?: string;
  pulseCdnOptions?: PulseCdnOptions;
}) {
  const [cdnBrowser, setCdnBrowser] = useState<PulseCdnBrowser>();
  const [cdnLoadError, setCdnLoadError] = useState<string>('');
  const [cdnLoading, setCdnLoading] = useState<boolean>(true);

  const { accountStore } = useStore();

  useEffect(() => {
    initializePulseCdn({
      initialPath,
      getOptions: accountStore.fetchPulseCdnOptions,
      pulseCdnOptions,
    })
      .then((bb) => {
        setCdnLoading(false);
        setCdnBrowser(bb);
      })
      .catch((e) => {
        setCdnLoading(false);
        setCdnLoadError(e instanceof Error ? e.message : JSON.stringify(e));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { cdnBrowser, cdnLoadError, cdnLoading };
}

function parsePulseCdnOptions(
  params: URLSearchParams,
): PulseCdnOptions | undefined {
  const raw = params.get('pulseCdnOptions');
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as PulseCdnOptions;
  } catch {
    return undefined;
  }
}
